import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../i18n/LanguageContext";
import "./components/complete-profile.css";

function CompleteProfile() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

  const tr = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const [firstname, setFirstname] = useState(localStorage.getItem("firstname") || "");
  const [lastname, setLastname] = useState(localStorage.getItem("lastname") || "");
  const [countrycode, setCountrycode] = useState("977");
  const [phnumber, setPhnumber] = useState("");
  const [gender, setGender] = useState("");
  const [role, setRole] = useState(localStorage.getItem("role") || "renter");
  const [countryCodes, setCountryCodes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadCountryCodes = async () => {
      try {
        const targetUrl = apiBaseUrl ? `${apiBaseUrl}/api/countrycode` : "/api/countrycode";
        const req = await fetch(targetUrl);
        const data = await req.json();
        if (!Array.isArray(data) || cancelled) return;

        const sorted = [...data].sort((a, b) => {
          const aPhone = Number.parseInt(a?.phone, 10) || 0;
          const bPhone = Number.parseInt(b?.phone, 10) || 0;
          if (aPhone === bPhone) {
            return String(a?.alpha_3 || "").localeCompare(String(b?.alpha_3 || ""));
          }
          return aPhone - bPhone;
        });

        setCountryCodes(sorted);

        const hasDefault = sorted.some((item) => String(item?.phone) === "977");
        if (!hasDefault && sorted[0]?.phone) {
          setCountrycode(String(sorted[0].phone));
        }
      } catch (_) {
        // Keep safe fallback value when the country list cannot be loaded.
      }
    };

    loadCountryCodes();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  const phoneDigits = phnumber.replace(/\D/g, "");
  const phoneHint = phoneDigits && phoneDigits.length < 7
    ? tr("oauthProfile.phoneHint", "Enter at least 7 digits.")
    : "";

  const submitProfile = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!firstname.trim()) {
      setFormError(tr("oauthProfile.firstNameRequired", "First name is required."));
      return;
    }

    if (!lastname.trim()) {
      setFormError(tr("oauthProfile.lastNameRequired", "Last name is required."));
      return;
    }

    if (!countrycode.trim()) {
      setFormError(tr("oauthProfile.countryCodeRequired", "Country code is required."));
      return;
    }

    if (!phnumber.trim()) {
      setFormError(tr("oauthProfile.phoneRequired", "Phone number is required."));
      return;
    }

    if (!gender) {
      setFormError(tr("oauthProfile.genderRequired", "Please select your gender."));
      return;
    }

    if (!["owner", "renter"].includes(role)) {
      setFormError(tr("oauthProfile.roleRequired", "Please select a role."));
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    setIsSaving(true);

    try {
      const targetUrl = apiBaseUrl ? `${apiBaseUrl}/api/auth/complete-profile` : "/api/auth/complete-profile";
      const res = await fetch(targetUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ firstname, lastname, countrycode, phnumber, gender, role }),
      });

      const data = await res.json();

      if (!res.ok) {
        setFormError(data.message || tr("oauthProfile.submitFailed", "Could not complete profile. Please try again."));
        setIsSaving(false);
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.role);
      localStorage.setItem("firstname", data.firstname || "");
      localStorage.setItem("lastname", data.lastname || "");
      localStorage.setItem("profileCompleted", "true");
      localStorage.setItem("authProvider", "google");

      if (data.role === "owner") {
        navigate("/owner-dashboard", { replace: true });
        return;
      }

      navigate("/renter-dashboard", { replace: true });
    } catch (_) {
      setFormError(tr("oauthProfile.networkError", "Network error. Please try again."));
      setIsSaving(false);
    }
  };

  return (
    <section className="oauth-setup-page container py-5">
      <div className="oauth-setup-shell">
        <aside className="oauth-setup-aside">
          <div className="oauth-brand-pill">Google OAuth</div>
          <h2>{tr("oauthProfile.title", "Complete Your Profile")}</h2>
          <p>{tr("oauthProfile.subtitle", "Finish these required details to complete your Google account setup.")}</p>

          <div className="oauth-benefits">
            <div className="oauth-benefit-item">{tr("oauthProfile.tipVerified", "Verified account flow with secure session handoff")}</div>
            <div className="oauth-benefit-item">{tr("oauthProfile.tipPrivacy", "Your details are used only for trusted rental matching")}</div>
            <div className="oauth-benefit-item">{tr("oauthProfile.tipEditable", "You can edit these details later from your profile")}</div>
          </div>
        </aside>

        <div className="oauth-setup-card">
          <div className="oauth-form-header">
            <span className="oauth-step-badge">{tr("oauthProfile.stepLabel", "Step 2 of 2")}</span>
            <h3>{tr("oauthProfile.formTitle", "Account Details")}</h3>
          </div>

          {formError && <div className="alert alert-danger">{formError}</div>}

          <form onSubmit={submitProfile} className="d-grid gap-3">
            <div className="oauth-grid-2">
              <div>
                <label className="form-label">{tr("oauthProfile.firstName", "First Name")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  placeholder={tr("oauthProfile.firstNamePlaceholder", "Enter first name")}
                  maxLength={100}
                  required
                />
              </div>

              <div>
                <label className="form-label">{tr("oauthProfile.lastName", "Last Name")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  placeholder={tr("oauthProfile.lastNamePlaceholder", "Enter last name")}
                  maxLength={100}
                  required
                />
              </div>
            </div>

            <div>
              <label className="form-label">{tr("oauthProfile.phone", "Phone Number")}</label>
              <div className="phone-input-row">
                <div className="phone-code-select">
                  <select
                    className="form-select"
                    value={countrycode}
                    onChange={(e) => setCountrycode(e.target.value)}
                    required
                  >
                    <option value="977">NPL +977</option>
                    {countryCodes.map((item, idx) => {
                      const phone = String(item?.phone || "").trim();
                      if (!phone || phone === "977") return null;
                      return (
                        <option value={phone} key={`${phone}-${item?.alpha_3 || idx}-${idx}`}>
                          {`${item?.alpha_3 || "CC"} +${phone}`}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="flex-fill">
                  <input
                    type="tel"
                    className="form-control"
                    value={phnumber}
                    onChange={(e) => setPhnumber(e.target.value)}
                    placeholder={tr("oauthProfile.phonePlaceholder", "98XXXXXXXX")}
                    maxLength={20}
                    required
                  />
                </div>
              </div>
              {phoneHint && <small className="text-muted">{phoneHint}</small>}
            </div>

            <div className="oauth-grid-2">
              <div>
                <label className="form-label">{tr("oauthProfile.gender", "Gender")}</label>
                <select
                  className="form-select"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  required
                >
                  <option value="">{tr("oauthProfile.genderSelect", "Select gender")}</option>
                  <option value="male">{tr("oauthProfile.genderMale", "Male")}</option>
                  <option value="female">{tr("oauthProfile.genderFemale", "Female")}</option>
                  <option value="other">{tr("oauthProfile.genderOther", "Other")}</option>
                  <option value="prefer_not_to_say">{tr("oauthProfile.genderNone", "Prefer not to say")}</option>
                </select>
              </div>

              <div>
                <label className="form-label">{tr("oauthProfile.role", "Role")}</label>
                <select
                  className="form-select"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  required
                >
                  <option value="owner">{tr("oauthProfile.roleOwner", "Property Owner")}</option>
                  <option value="renter">{tr("oauthProfile.roleRenter", "Renter")}</option>
                </select>
              </div>
            </div>

            <button type="submit" className="btn btn-primary oauth-submit-btn mt-2" disabled={isSaving}>
              {isSaving ? tr("oauthProfile.saving", "Saving...") : tr("oauthProfile.submit", "Complete Account")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default CompleteProfile;
